import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { Col, Row } from "react-styled-flexboxgrid";
import PageSection, { StyledSectionHeading } from "../components/PageSection";
import { siteTitle } from "../data/PublicSiteData";
import { trackPageView } from "../includes/analytics";
import Public from "../Layouts/Public";
import { Color } from "../theme/Color";

const Terms = () => {
  const router = useRouter();

  trackPageView("/terms-of-use");

  return (
    <>
      <NextSeo
        title={`Privacy Policy | ${siteTitle}`}
        description="Valmeego is a new way for destinations and visitors to interact online.
Get inspiration, local knowledge, advice & deals directly from any destination."
      />
      <PageSection
        backgroundColor={Color.White}
        width="1340px"
        data-cy="core-values"
      >
        <StyledSectionHeading center="xs">Privacy Policy</StyledSectionHeading>
        <Row center="xs" middle="xs">
          <Col
            xs={10}
            md={8}
            style={{ color: Color.Grey2, marginBottom: "5rem" }}
          >
            <h2>1. Introduction</h2>
            <p>
              When you use Valmeego, you trust us with your personal data. We're
              committed to keeping that trust. That starts with helping you
              understand our privacy practices.
            </p>
            <p>
              This notice describes the personal data we collect, how it is used
              and shared, and your choices regarding this data. We recommend
              that you read our privacy overview, which highlights key points
              about our privacy practices.
            </p>

            <h2>2. Overview</h2>
            <p>
              This notice applies to users of Valmeego's service anywhere in the
              world, including users of Valmeego's websites, Progressive web
              Application, features and other services.
            </p>
            <p>This notice specifically applies to:</p>
            <ul>
              <li>
                <b>Trip Planners and Participants:</b> Individuals who use and
                plan their trips on Valmeego.
              </li>
              <li>
                <b>Ameegos:</b> Individuals who provide knowledge and
                recommendations to Trip planners through Valmeego.
              </li>
              <li>
                <b>Service Providers:</b> Individuals who provide services to
                Valmeego's users via the travel feed.
              </li>
            </ul>
            <p>
              This notice also governs Valmeegos other collections of personal
              data in connection with its services. For example, we may collect
              the contact information of individuals who use accounts owned by
              Valmeego for Business customers; personal data of those who start
              but do not complete applications to be Ameego's or service
              providers; or other personal data in connection with our mapping
              technology and features. All those subject to this notice are
              referred to as “users”. Our practices are subject to applicable
              laws in the places in which we operate. This means that we engage
              in the practices described in this notice in a particular country
              or region, if permitted under their laws.
            </p>

            <h2>3. Data controller and transfer</h2>
            <p>
              Valmeego Ltd are the data controllers for the personal data
              collected with use of Valmeego's services in the European Economic
              Area, the United Kingdom and elsewhere.
            </p>
            <p>
              Valmeego operates, and processes personal data, globally. The
              exercise of data protection rights can be directed at any
              controller mentioned above, preferably via the designated
              channels, and will be handled by Valmeego as a group. We may also
              transfer such data to countries other than the one where our users
              live or use Valmeego's services. We do so in order to fulfil our
              agreements with users, such as our Terms of Use, or based on
              users' prior consent, adequacy decisions for the relevant
              countries.
            </p>

            <h2>4. Data collections and uses</h2>
            <h3 style={{ margin: "2rem 0" }}>4.1 The data we collect:</h3>
            <h4>4.1.1 Valmeego collects personal data</h4>
            <ul>
              <li>
                Provided by users to Valmeego, such as during account creation.
              </li>
              <li>
                Created during use of our services, such as preferences, app
                usage and device data.
              </li>
              <li>
                From other sources, such as other users, business servicesm
                Ameego's, destination promoers and governmental authorities.
              </li>
            </ul>

            <p>
              The following <b>personal data</b> is collected by or on behalf of
              Valmeego:
            </p>
            <h4>Data provided by users:</h4>
            <ul>
              <li>
                <b>User Profile:</b> We collect data when users create their
                Valmeego accounts. This may include their name, email, phone
                number, email, password, profile picture, business name,
                nationality , location, and languages. This also includes gender
                and occupation when required for certain Valmeego services such
                as becoming an Ameego.
              </li>
              <li>
                <b>Demographic Data:</b>We may collect demographics data about
                users, including through user surveys. In some countries, we may
                also receive demographic data about users from third parties.
              </li>
              <li>
                <b>User Content: </b>We collect the data submitted by users when
                they contact Valmeego costumer service, provide ratings or
                recommendations for other users, businesses, Ameego's or
                otherwise contact Valmeego. This may include feedback,
                photographs and audio or video recordings collected by users in
                connection with customer support.
              </li>
              <li>
                <b>Travel Information: </b>We collect travel plan information,
                including the times and dates trip plans begin and end,
                lodgings, and transport times from users through our itinerary
                feature.
              </li>
            </ul>

            <h4>Data created during use of services</h4>
            <ul>
              <li>
                <p>
                  <b>Travel data (Trip Planners and Trip Participants):</b> We
                  collect Trip Planners precise number of trips created, amount
                  of Ameego's used, searchable feed and location data to enhance
                  use of our PWA. Enabling Valmeego to detect and prevent fraud
                  and to satisfy legal requirements.
                </p>

                <p>
                  Valmeego collects this information when Valmeego's progressive
                  web application (PWA) is running in the foreground (app open
                  and on-screen) or background (app open but not on-screen) of
                  their technology device.
                </p>
              </li>
              <li>
                <p>
                  <b>Travel data (Service Providers and Ameego's)</b> We collect
                  Service providers and Ameego's interactions to enable and
                  enhance use of our pwa, including to improve Ameego's
                  performance and recommendations, facilitate bookings, and
                  detect fraud.
                </p>
                <p>
                  We collect such data from user's mobile devices if they enable
                  us to do so. Valmeego collects such data from the time users
                  plan, book, up until the trip is completed, and any time the
                  app is running in the foreground of the mobile device.
                </p>
              </li>
              <li>
                <b>Transaction Information:</b> Valmeego uses Stripe payment
                infrastructure to accept payments and store all transactional
                information such as card details, name, and amount of
                transaction, date, and time the booking was made, amount charge,
                and payment method.
              </li>
              <li>
                <b>Usage Data:</b> We collect data about how users interact with
                our services. This includes data such as access dates and times,
                pwa features, page views, pwa crashes and other system activity.
                We may also collect data regarding the third-party services used
                before interacting with our services, which we use for
                marketing.
                <p>
                  In some case, we collect this data through cookies, and tags.
                </p>
              </li>
              <li>
                <b>Device data:</b>We may collect data about the devices used to
                access our services, including the hardware models, IP address
                or other unique device identifiers, operating systems, software,
                preferred languages, advertising identifies, and mobile network
                data.
              </li>
              <li>
                <b>Communication data:</b> We enable users to communicate with
                each other through Valmeego's Chat Channels. Users can
                communicate to all user groups through Valmeego's pwa and
                website. For Example, Valmeego enables all users to have a
                direct line of contact to each other. (Generally, without
                disclosing their telephone numbers to each other) To provide
                this service, Valmeego receives some data regarding the texts
                including the data and time of the communications and the
                content of communications.
                <p>
                  Valmeego use this data for safety, security purposes, to
                  improve our services and features, and for analytics.{" "}
                </p>
              </li>
            </ul>

            <h4>Data from other sources</h4>
            <ul>
              <li>
                Valmeego account owners who request services for or on behalf of
                other users, or who enable such users to request or receive
                services through their accounts. This includes owners of
                Valmeego for Business accounts
              </li>
              <li>
                Users or others providing information in connection with claims
                or disputes.
              </li>
              <li>
                Valmeego business partners through which users create or access
                their Valmeego account, such as payment providers, social media
                services, apps or websites that use Valmeego's APIs or whose
                APIs Valmeego uses.
              </li>
              <li>Publicly available sources</li>
              <li>
                Marketing service providers whose data Valmeego use for
                marketing or research.
              </li>
              <li>
                Law enforcement officials, public health officials and other
                government authorities.
              </li>
            </ul>

            <h4>4.1.2 How we use personal data:</h4>
            <p>
              Valmeego uses personal data to enable secure trip planning,
              convenient booking, and other services. Valmeego does not sell or
              share user personal data with third parties for their direct
              marketing, except with users' consent.
            </p>
            <p>We also use data:</p>
            <ul>
              <li>
                to enhance the safety and security of our users and services
              </li>
              <li>for customer support</li>
              <li>to enable communications between users</li>
              <li>
                to send marketing and non-marketing communications to users
              </li>
              <li>for research and development</li>
              <li>in connection with legal proceedings</li>
            </ul>
            <p>
              Valmeego uses data to provide, personalize, maintain, and improve
              our services for all users. This includes using data to:
            </p>
            <ul>
              <li>Create and, or update accounts</li>
              <li>
                Enable booking and recommendation services, and features that
                involve data sharing (ratings)
              </li>
              <li>Track and share the progress of Ameego's </li>
              <li>
                Personalize user's travel feed. For example, we may present
                users with personalized deals, services, recommendations based
                on prior searches and bookings.
              </li>
              <li>
                Perform necessary operations to maintain our services (such as
                troubleshoot software bugs to conduct data analysis, testing and
                research. We monitor and analyse usage and activity trends on
                Valmeego)
              </li>
            </ul>

            <h5>Safety and security</h5>
            <p>
              We use personal data to maintain the safety, security and
              integrity of our service and users. This includes:
            </p>

            <ul>
              <li>
                Verifying users' identity and eligibility to access Valmeego's
                platform.
              </li>
              <li>
                Using data from services and Ameego's to detect unsafe behaviour
                such as fraud, and mistreatment of information.
              </li>
              <li>
                Using devices, user profiles, location, usage data to prevent,
                detect and combat fraud. Identifying fraudulent accounts or uses
                of our services, preventing unauthorised Ameego's or travel
                services, verifying user identities in connection with certain
                security and payment methods.
              </li>
              <li>
                Using user ratings, reported incidents, and other feedback as
                grounds for deactivating users with low ratings or who have
                violated their guidelines.
              </li>
            </ul>

            <p>
              Valmeego performs the above activities on the grounds that they
              are necessary to fulfil our obligations to users under the Terms
              of Use or other agreements with users, and/or for purposes of the
              legitimate safety and security interests of Valmeego or other
              parties, including users and members of the public.
            </p>

            <h5>Customer Support</h5>
            <p>
              Valmeego uses the information we collect to provide customer
              service, addressing user concerns, monitoring, and improving our
              customer support responses and processes.
            </p>

            <h5>Enabling communication between users.</h5>
            <p>
              A Trip Planner can message any user directly through Valmeego. For
              example, a Trip planner may request a quote or make bookings
              through services advertised on Valmeego. Valmeego performs the
              above activities on the ground that they are necessary to fulfil
              our obligations to users under our Terms of Use or other
              agreements with users.
            </p>

            <h5>Marketing</h5>
            <p>
              Valmeego may use personal data to market our services to other
              users. This includes sending Valmeego's users communications about
              our services, features, promotions, studies, news, updates, and
              events. We may do so by email, push notifications, in-app
              communications, ads and on third party platforms.
            </p>

            <h5>Non-marketing Communications</h5>
            <p>
              Valmeego may use personal data to generate and provide users with
              receipts; inform them of changes to our terms, services, or
              policies. Valmeego performs the above actives on the grounds that
              they are necessary to fulfil our obligations to users under our
              Terms of Use.
            </p>

            <h5>Automated decision-making</h5>
            <p>
              We use personal data to make automated decisions relating to use
              of our services. This includes:
            </p>
            <ul>
              <li>
                Determining user ratings, and deactivating users with low
                ratings. In the European Union or where otherwise required by
                law, such deactivation occurs only after human review and/or the
                possibility to appeal.{" "}
              </li>
              <li>
                Matching available Ameego's or businesses to users requesting
                services. Users can be matched based on preferences, proximity,
                availability, and other factors.
              </li>
              <li>
                Flagging users who are identified as having engaged in fraud,
                unsafe activity, or other activities that may harm Valmeego, its
                users and others. In some cases, such as when a user is
                determined to be abusing Valmeego's referral program or has
                submitted fraudulent documents, such behaviour may result in
                automatic deactivation, or in the European Union or where
                otherwise required by law, deactivation after human review.
              </li>
            </ul>

            <h2>5. Cookies and third-party disclosure</h2>
            <p>
              Valmeego uses cookies and other identification technologies on our
              website, Progressive web application, emails and online ads for
              purposes described in this notice and Valmeego's Cookie Notice.
            </p>
            <p>
              A Cookie is a small piece of data that is stored on your browser
              or device by websites, progressive web applications, online media
              and advertisements that are used to remember your browser/ device
              during and across website visits. Valmeego uses cookies for such
              purposes such as:
            </p>
            <ul>
              <li>Authenticating users</li>
              <li>
                Improving users by remembering use preferences and settings
              </li>
              <li>Determining the popularity of content</li>
              <li>
                Delivering and measuring the effectiveness of our advertising
                campaigns
              </li>
              <li>
                Analysing site traffic and trends and generally understanding
                the online behaviours and interest of users who interact with
                our services.
              </li>
            </ul>
            <p>
              We may also allow others to provide audience measurement and
              analytics services for us, to serve advertisements on our behalf
              across the internet. Please see our cookie policy for more
              information regarding the use of cookies.
            </p>

            <h2>6. Data sharing and disclosure</h2>
            <p>
              Some of Valmeego's service and features require that we share
              personal data with other users or at a user's request.{" "}
            </p>
            <p>
              We may also share such data with our affiliates, subsidiaries, and
              partners, for legal reasons or in connection with claims or
              disputes.
            </p>
            <p>Valmeego may share personal data: </p>

            <h3>6.1 With other users</h3>
            <p>This includes sharing:</p>
            <ul>
              <li>
                Ameego's and services name, ratings, occupation, type of
                service, and profile pictures.
              </li>
              <li>
                Trip Planners profile information such as contact details, name,
                profile picture, number of group participants, nationality.
              </li>
              <li>
                For Ameego's and businesses, we may share their data with Trip
                Planners, Trip Participants, and other Ameego's or Businesses
                such as, business name, profile picture, cover picture, contact
                information (if permitted by applicable laws), average rating
                provided by users, period of time since they signed up to be an
                Ameego and/or business.
              </li>
              <li>
                We also provide Trip Planners and order recipients with receipts
                containing information such as the breakdown of amounts charged
                per booking. We also include other information on those receipts
                required by law.
              </li>
            </ul>

            <h3>6.2 At the User's Request</h3>
            <p>This includes sharing data with:</p>
            <ul>
              <li>
                Other people at the user's request. For example, we share a
                user's location with a friend when requested by that users, or a
                user's trip information.
              </li>
              <li>
                Emergency services. We offer features that enable users to their
                data with police, fire, and ambulance services in the event of
                an emergency or after certain incidents.{" "}
              </li>
            </ul>

            <h3>6.3 With the public</h3>
            <p>
              Questions or comments from users through Valmeego's social media
              pages may be viewable by the public, including any personal data
              included in the questions or comments submitted by a user.
            </p>

            <h3>6.4 With the Valmeego account owner</h3>
            <p>
              If a user makes a booking owned by another party, we may share
              their order of trip information.
            </p>

            <h3>6.5 With Valmeego service providers and business partners</h3>
            <p>
              Valmeego provides personal data with our subsidiaries to help us
              provide our services or conduct data processing on our behalf.
            </p>
            <p>These include:</p>
            <ul>
              <li>
                Service Providers from whom order recipients make bookings
                and/or the point of sale providers, including the order
                fulfilment, communications and marketing purposes.
              </li>
              <li>Payment processors and facilitators</li>
              <li>Background check and identity verification providers</li>
              <li>
                Google in connecting with the use of Google Maps in Valmeego's
                pwa (see Google's privacy policy for information on their
                collection and use of data)
              </li>
              <li>
                Social media companies, including Facebook, twitter in
                connection with Valmeego's use of their tools in Valmeego's pwa
                and websites. (See Facebook's and Twitter privacy policies for
                information on their collection and use of data)
              </li>
              <li>
                Marketing platform providers, including social media advertising
                services, advertising networks, third-party providers, and other
                service providers to reach or better understand our users and
                measure advertising effectiveness.
              </li>
              <li>
                Consultants, lawyers, accountants, and other professional
                service providers
              </li>
              <li>Insurance and financing partners</li>
            </ul>

            <h3>6.6 For Legal reasons</h3>
            <p>
              Valmeego may share User's personal data if we believe it is
              required by applicable law, regulation, operating license, legal
              process, or governmental request. This includes sharing personal
              data with law enforcement officials, public health officials, and
              other government authorities as necessary to enforce our terms of
              Service, and user agreements. In the event of a dispute relating
              to use of an others person's credit card, we may be required by
              law to share your personal data, including trip, booking
              information, with the owner of that credit card.
            </p>

            <h3>6.7 With Consent</h3>
            <p>
              Valmeego may share user's personal data other than described in
              notice if we notify the user and they consent to sharing.
            </p>

            <h2>7. Data Retention and deletion</h2>
            <p>
              Valmeego retains user data for as long as necessary for the
              purpose described above. Users may request deletion of their
              accounts at any time.
            </p>
            <p>
              Valmeego may retain user data after a deletion request due to
              legal or regulatory requirements or for reason stated in this
              policy. Valmeego retains user data for as long as necessary for
              the purpose described above. This means we retain different
              categories of data for different period of time depending on the
              type of data, whom the data relates to, and the purpose for which
              we collected the data.
            </p>
            <p>
              Users may request deletion of their account at any time through
              the Profile Settings in the Valmeego pwa or through the Valmeego
              website.
            </p>
            <p>
              Following an account deletion request, Valmeego deletes the user's
              account and data, unless they must be retained due to legal or
              regulatory requirements, for purposes of safety and security.{" "}
            </p>

            <h2>8. Choice and Transparency</h2>
            <p>
              Valmeego enables users to access and/ or control the data Valmeego
              collects including through:
            </p>
            <ul>
              <li>Privacy settings</li>
              <li>Device permissions</li>
              <li>Marketing choices</li>
            </ul>
            <p>
              Valmeego also enables users to request access to or copies of
              their data, changes, updates to their accounts, or deletion of
              their accounts.
            </p>

            <ol>
              <li>
                <b>Privacy Settings</b>
                <p>
                  The settings in Valmeego's pwa allows users to set and update
                  their preferences regarding data collection and sharing,
                  emergency data sharing and notifications.
                </p>
              </li>
              <li>
                <b>Device permissions</b>
                <p>
                  Most mobile device platforms have defined certain type of data
                  that PWA cannot access without the device owner's permission,
                  these platforms have different methods for how that permission
                  can be obtained.{" "}
                </p>
              </li>

              <li>
                <b>In- app ratings pages</b>
                <p>
                  After every trip or booking users are able to rate each other
                  from a scale of one to five. An average of those ratings is
                  associated with a user's account and is displayed to other
                  users for whom they provide or receive services from. For
                  example, Trip Planners are able to rate Ameego's based on the
                  knowledge they have provided, and Ameego's are in turn able to
                  rate the Trip Planner and group trips they have been in
                  contact with.
                </p>
                <p>
                  This two-way system holds everyone accountable for their
                  behaviour. Accountability helps create a respectful, safe
                  environment for Valmeego users.
                </p>
                <ul>
                  <li>
                    Trip Planners can see their average rating on the personal
                    profile.
                  </li>
                  <li>
                    Services can see their average rating after clicking into
                    their public profile.
                  </li>
                </ul>
              </li>
              <li>
                <b>Marketing Choices</b>
                <p>
                  Users may opt out of certain marketing communications and use
                  of their data for marketing in the following ways:
                </p>

                <p>
                  <b>Marketing emails and messages:</b> To opt out of receiving
                  marketing emails from Valmeego, users can choose to
                  unsubscribe. We may still send users who have opted out non-
                  promotional communications, such as receipts for trips or
                  information about their account.
                </p>
                <p>
                  <b>Cookies: </b>For information on how to opt out of
                  personalised ads using cookies and related technologies,
                  please see our Cookie notice.
                </p>
                <b>User Personal Data request</b>
                <p>
                  Valmeego provides user with a variety of ways to learn about,
                  control and submit questions and comments on Valmeego's
                  handling of their personal data.
                </p>
                <p>
                  <b>Accessing data: </b>Users can access data including their
                  profile data and trip history through the Valmeego pwa or
                  website.
                </p>
                <p>
                  <b>Changing or updating data: </b>Users can edit the name,
                  phone number, email address, payment method and photo
                  associated with their account through the settings in their
                  profile.
                </p>

                <p>
                  <b>Deleting data</b> Users may request deletion of their
                  account at any time through the profile settings in the
                  Valmeego pwa, or Valmeego's website.
                </p>

                <p>
                  <b>Objections, restrictions, and complaints: </b> Users may
                  request that we stop using all or some of their personal data.
                  This includes objecting to our use of personal data that is
                  based on Valmeego's legitimate interests. Valmeego may
                  continue to process data after such objection or request to
                  the extent required or permitted by law.
                </p>
              </li>
            </ol>
            <h2>9. Changes to this privacy policy:</h2>
            <p>
              We may update this privacy policy from time to time. If we make
              any material changes, we will notify you prior to the changes
              taking effect by posting a notice on our website.
            </p>
          </Col>
        </Row>
      </PageSection>
    </>
  );
};

Terms.Layout = Public;

export default Terms;
